
import React from 'react';
import ModernPolicyLayout from '@/components/legal/ModernPolicyLayout';

const Disclaimers = () => {
  const tableOfContents = [
    { id: 'general', title: 'General Disclaimer', level: 1 },
    { id: 'accuracy', title: 'Calculation Accuracy', level: 1 },
    { id: 'calculator-specific', title: 'Calculator-Specific Disclaimers', level: 1 },
    { id: 'not-advice', title: 'Not Financial Advice', level: 1 },
    { id: 'beta-software', title: 'Beta Software Disclaimer', level: 1 },
    { id: 'data-inputs', title: 'Data Input Responsibility', level: 1 },
    { id: 'ai-automation', title: 'AI & Automation Disclaimer', level: 1 },
    { id: 'market-conditions', title: 'Market Conditions', level: 1 },
    { id: 'real-time-data', title: 'Real-Time Data Disclaimer', level: 1 },
    { id: 'third-party', title: 'Third-Party Information', level: 1 },
    { id: 'professional-services', title: 'Professional Services Disclaimer', level: 1 },
    { id: 'regional', title: 'Regional Variations', level: 1 },
    { id: 'loan-approval', title: 'Loan Approval Disclaimer', level: 1 },
    { id: 'liability', title: 'Limitation of Liability', level: 1 },
    { id: 'contact', title: 'Contact Information', level: 1 }
  ];

  return (
    <ModernPolicyLayout 
      title="Financial Disclaimers" 
      estimatedReadTime="12 min read"
      lastUpdated="Last updated: October 10, 2025"
      tableOfContents={tableOfContents}
    >
      <div className="space-y-8">
        <section id="general">
          <h2>General Disclaimer</h2>
          <p>
            The calculators and information provided in EquiForge™ Homebuyer Optimizer are for 
            general informational and educational purposes only. They are not intended to provide 
            specific financial, investment, tax, legal, or accounting advice. All calculations 
            are estimates and should not be considered as professional financial guidance.
          </p>
        </section>

        <section id="accuracy">
          <h2>2. Accuracy of Calculations</h2>
          <p>
            The results provided by our calculators are estimates based on the information you 
            provide and assumptions that may not apply to your individual circumstances. Actual 
            loan terms, payment amounts, and eligibility may vary significantly based on:
          </p>
          <ul>
            <li><strong>Credit Score:</strong> Your actual credit score and credit history</li>
            <li><strong>Debt-to-Income Ratio:</strong> All monthly debt obligations and income sources</li>
            <li><strong>Employment History:</strong> Stability and length of employment</li>
            <li><strong>Asset Verification:</strong> Down payment source and reserves</li>
            <li><strong>Property Factors:</strong> Property type, location, and appraisal value</li>
            <li><strong>Lender Requirements:</strong> Specific underwriting guidelines vary by lender</li>
          </ul>
        </section>

        <section id="calculator-specific">
          <h2>3. Calculator-Specific Disclaimers</h2>
          <p>
            Each calculator in EquiForge™ has specific limitations and assumptions. Please review 
            the following disclaimers for each tool:
          </p>

          <h3>Buying Power Calculator</h3>
          <p>
            This calculator provides an estimate of how much home you may be able to afford based 
            on your income, debts, and down payment. <strong>Important:</strong> This is NOT a 
            pre-qualification or pre-approval. The buying power estimate:
          </p>
          <ul>
            <li>Assumes standard debt-to-income ratios (typically 43% backend, 28% frontend)</li>
            <li>Does not account for your actual credit score or credit history</li>
            <li>Does not verify your income or employment</li>
            <li>May not reflect lender-specific overlays or requirements</li>
            <li>Does not guarantee loan approval or specific loan terms</li>
          </ul>
          <p>
            <strong>Action Required:</strong> To get an accurate pre-qualification or pre-approval, 
            you must contact a licensed mortgage lender who will verify all information and review 
            your complete financial profile.
          </p>

          <h3>DTI (Debt-to-Income) Calculator</h3>
          <p>
            The DTI calculator helps you understand your debt-to-income ratio. However:
          </p>
          <ul>
            <li>Lender DTI requirements vary by loan program (FHA, conventional, VA, jumbo)</li>
            <li>Some lenders accept higher DTI ratios with compensating factors</li>
            <li>Our calculation uses standard formulas but lenders may calculate differently</li>
            <li>Not all debts may be counted the same way by all lenders</li>
            <li>Child support, alimony, and other obligations may affect your DTI</li>
          </ul>

          <h3>Mortgage Calculator</h3>
          <p>
            The mortgage payment calculator provides estimates of principal, interest, taxes, and 
            insurance (PITI). Please note:
          </p>
          <ul>
            <li>Interest rates shown may not reflect current market rates or rates you qualify for</li>
            <li>Property tax estimates are based on general data and may not match your specific property</li>
            <li>Insurance estimates are averages and actual costs vary by property, location, and coverage</li>
            <li>HOA fees and special assessments are not included unless manually added</li>
            <li>PMI calculations are estimates and actual PMI rates vary by lender and loan-to-value ratio</li>
            <li>Does not include closing costs, origination fees, or other one-time expenses</li>
          </ul>

          <h3>SmartBuy Analyzer</h3>
          <p>
            The SmartBuy Analyzer compares properties and scenarios. This tool:
          </p>
          <ul>
            <li>Is not a substitute for a professional home inspection or appraisal</li>
            <li>Does not account for property condition, needed repairs, or hidden defects</li>
            <li>Cannot predict future property values or market appreciation</li>
            <li>Does not consider neighborhood factors, school districts, or quality of life metrics</li>
            <li>Should be used as one of many factors in your home buying decision</li>
          </ul>

          <h3>Property Tax & HOA Database</h3>
          <p>
            Property tax and HOA fee information is sourced from public records and third-party data. 
            Please be aware:
          </p>
          <ul>
            <li>Tax and HOA data may be outdated or inaccurate</li>
            <li>Property taxes can change annually based on reassessments</li>
            <li>HOA fees can increase and special assessments may be levied</li>
            <li>Transfer taxes or supplemental taxes may apply when purchasing property</li>
            <li>Always verify tax and HOA information with local authorities and HOA boards</li>
          </ul>
        </section>

        <section id="not-advice">
          <h2>4. Not Financial Advice</h2>
          <p>
            The information provided by EquiForge™ Homebuyer Optimizer should not be construed 
            as financial or investment advice. You should consult with qualified professionals 
            such as:
          </p>
          <ul>
            <li><strong>Loan Officers:</strong> For specific mortgage product information and pre-qualification</li>
            <li><strong>Mortgage Brokers:</strong> For comparing multiple lender options</li>
            <li><strong>Financial Advisors:</strong> For comprehensive financial planning</li>
            <li><strong>Accountants:</strong> For tax implications of home ownership</li>
            <li><strong>Real Estate Attorneys:</strong> For legal aspects of property transactions</li>
            <li><strong>Real Estate Agents:</strong> For market conditions and property selection</li>
          </ul>
        </section>

        <section id="beta-software">
          <h2>5. Beta Software Disclaimer</h2>
          <p>
            EquiForge™ Homebuyer Optimizer may include features or functionality currently in beta, 
            preview, or testing phases. By using these beta features, you acknowledge:
          </p>
          <ul>
            <li>
              <strong>Work in Progress:</strong> The app is under active development and may contain 
              bugs, errors, or incomplete features
            </li>
            <li>
              <strong>No Warranty:</strong> Beta features are provided "as-is" without warranties of 
              any kind regarding accuracy, reliability, or performance
            </li>
            <li>
              <strong>Data Loss Risk:</strong> Beta features may result in unexpected behavior, data 
              loss, or calculation errors
            </li>
            <li>
              <strong>Changes Without Notice:</strong> Features may be modified, removed, or 
              discontinued at any time without prior notification
            </li>
            <li>
              <strong>Limited Support:</strong> Beta features may have limited or no customer support
            </li>
            <li>
              <strong>Feedback Welcome:</strong> We appreciate bug reports and feedback to improve 
              the app (support@equiforge.com)
            </li>
          </ul>
          <p>
            <strong>Critical Decisions:</strong> Do not rely solely on beta features for important 
            financial decisions. Always verify information through official channels and professional advisors.
          </p>
        </section>

        <section id="data-inputs">
          <h2>6. Data Input Responsibility</h2>
          <p>
            The accuracy of the calculator results depends entirely on the accuracy and completeness 
            of the information you provide. EquiForge™ is not responsible for errors in calculations 
            resulting from:
          </p>
          <ul>
            <li>Incorrect or incomplete information provided by users</li>
            <li>Outdated financial information or changed circumstances</li>
            <li>Misunderstanding of input field requirements</li>
            <li>Failure to include all sources of income or debt obligations</li>
          </ul>
        </section>

        <section id="ai-automation">
          <h2>7. AI & Automation Disclaimer</h2>
          <p>
            EquiForge™ may use artificial intelligence, machine learning, and automated data analysis 
            to provide property insights, market analysis, and recommendations. You acknowledge:
          </p>
          <ul>
            <li>
              <strong>AI Limitations:</strong> AI-generated insights are based on algorithms and 
              historical data that may not account for unique circumstances or future events
            </li>
            <li>
              <strong>No Guarantees:</strong> AI predictions, valuations, and recommendations are 
              estimates and should not be relied upon as accurate or complete
            </li>
            <li>
              <strong>Model Accuracy:</strong> Machine learning models have inherent limitations and 
              may produce inaccurate or biased results
            </li>
            <li>
              <strong>Human Verification Required:</strong> Always verify AI-generated information 
              with licensed professionals and official sources
            </li>
            <li>
              <strong>Not Professional Analysis:</strong> AI insights do not constitute professional 
              appraisals, inspections, or financial analysis
            </li>
            <li>
              <strong>Continuous Improvement:</strong> Our AI models are continuously updated, which 
              may result in different outputs over time for the same inputs
            </li>
          </ul>
          <p>
            <strong>Example Use Cases:</strong> AI may be used for property valuation estimates, 
            market trend analysis, neighborhood comparisons, and automated document analysis. None 
            of these should replace professional services.
          </p>
        </section>

        <section id="market-conditions">
          <h2>8. Market Conditions</h2>
          <p>
            Interest rates, property taxes, insurance costs, and other factors used in calculations 
            may change frequently due to market conditions. The information provided might not 
            reflect current rates or costs in your specific area. Factors that can change include:
          </p>
          <ul>
            <li><strong>Interest Rates:</strong> Daily fluctuations based on market conditions</li>
            <li><strong>Property Taxes:</strong> Annual assessments and local tax rate changes</li>
            <li><strong>Insurance Costs:</strong> Regional risk factors and coverage requirements</li>
            <li><strong>HOA Fees:</strong> Association budget changes and special assessments</li>
            <li><strong>Closing Costs:</strong> Lender fees and local market practices</li>
          </ul>
        </section>

        <section id="real-time-data">
          <h2>9. Real-Time Data Disclaimer</h2>
          <p>
            EquiForge™ may display market data, interest rates, property values, and other information 
            that changes frequently. Please note:
          </p>
          <ul>
            <li>
              <strong>Data Delays:</strong> Market data may be delayed by minutes, hours, or days 
              depending on the source
            </li>
            <li>
              <strong>Third-Party Sources:</strong> Data is sourced from third-party providers and 
              we do not control its accuracy or timeliness
            </li>
            <li>
              <strong>No Guarantees:</strong> We do not guarantee the accuracy, completeness, or 
              currentness of any market data
            </li>
            <li>
              <strong>Informational Only:</strong> Real-time data is provided for informational 
              purposes only and should not be used for trading or investment decisions
            </li>
            <li>
              <strong>Data Provider Limitations:</strong> Data providers may experience outages, 
              errors, or service interruptions
            </li>
            <li>
              <strong>Verify Before Acting:</strong> Always verify current rates and data with 
              official sources before making decisions
            </li>
          </ul>
          <p>
            <strong>Data Sources:</strong> We may use data from mortgage rate APIs, MLS listings, 
            county tax records, census data, and other public and proprietary sources.
          </p>
        </section>

        <section id="third-party">
          <h2>10. Third-Party Information</h2>
          <p>
            Some information used in our calculators may come from third-party sources including 
            government databases, industry reports, and market data providers. While we strive 
            to use reliable sources, EquiForge™ does not guarantee the accuracy, completeness, 
            or reliability of third-party information.
          </p>
        </section>

        <section id="professional-services">
          <h2>11. Professional Services Disclaimer</h2>
          <p>
            EquiForge™ and its operators are not licensed as financial advisors, mortgage brokers, 
            real estate agents, attorneys, or tax professionals. We do not provide:
          </p>
          <ul>
            <li>
              <strong>Financial Planning Services:</strong> We are not registered investment advisors 
              or financial planners. Consult a licensed CFP or financial advisor for personalized 
              financial planning.
            </li>
            <li>
              <strong>Mortgage Brokerage:</strong> We do not originate loans, arrange financing, or 
              provide mortgage brokerage services. Contact licensed mortgage professionals for loan products.
            </li>
            <li>
              <strong>Real Estate Brokerage:</strong> We do not list, sell, or broker real estate 
              transactions. Work with licensed real estate agents for property transactions.
            </li>
            <li>
              <strong>Legal Services:</strong> We do not provide legal advice or representation. 
              Consult licensed attorneys for legal matters related to real estate or contracts.
            </li>
            <li>
              <strong>Tax Preparation:</strong> We do not prepare tax returns or provide tax advice. 
              Consult licensed CPAs or tax professionals for tax implications of home ownership.
            </li>
            <li>
              <strong>Home Inspection Services:</strong> We do not conduct property inspections or 
              appraisals. Hire licensed inspectors and appraisers before purchasing property.
            </li>
          </ul>
          <p>
            <strong>Professional Licensing:</strong> Real estate, mortgage, and financial services 
            are highly regulated. Ensure any professional you work with holds appropriate licenses 
            in your state.
          </p>
        </section>

        <section id="regional">
          <h2>12. Regional Variations Disclaimer</h2>
          <p>
            Real estate laws, mortgage regulations, tax codes, and market practices vary significantly 
            by state, county, and municipality. EquiForge™ provides general information that may not 
            apply to your specific location:
          </p>
          <ul>
            <li>
              <strong>State Laws:</strong> Real estate and mortgage laws differ by state. Some states 
              are "judicial foreclosure" states, others are "non-judicial." Title and escrow practices 
              vary by region.
            </li>
            <li>
              <strong>Local Regulations:</strong> Zoning laws, building codes, and property regulations 
              are determined by local jurisdictions
            </li>
            <li>
              <strong>Market Practices:</strong> Buyer/seller responsibilities for closing costs, 
              customary earnest money deposits, and inspection contingencies vary by market
            </li>
            <li>
              <strong>Tax Implications:</strong> Property taxes, transfer taxes, and tax deductions 
              vary by location. Consult local tax professionals.
            </li>
            <li>
              <strong>Insurance Requirements:</strong> Flood insurance, earthquake insurance, and 
              other coverage requirements depend on property location
            </li>
            <li>
              <strong>Licensing Requirements:</strong> Professional licensing requirements for mortgage 
              professionals and real estate agents vary by state
            </li>
          </ul>
          <p>
            <strong>Recommendation:</strong> Always consult local professionals who are familiar with 
            laws, regulations, and practices in your specific area.
          </p>
        </section>

        <section id="loan-approval">
          <h2>13. No Guarantee of Loan Approval</h2>
          <p>
            Using EquiForge™ Homebuyer Optimizer does not guarantee that you will be approved 
            for a mortgage or other loan product. Lending decisions are made by financial 
            institutions based on their own comprehensive underwriting criteria, which may include:
          </p>
          <ul>
            <li>Complete credit report analysis</li>
            <li>Income and employment verification</li>
            <li>Asset documentation and source verification</li>
            <li>Property appraisal and inspection</li>
            <li>Debt-to-income ratio calculations</li>
            <li>Cash reserves and down payment verification</li>
          </ul>
        </section>

        <section id="liability">
          <h2>14. Limitation of Liability</h2>
          <p>
            In no event shall EquiForge™, its affiliates, or their respective officers, directors, 
            employees, or agents be liable for any direct, indirect, incidental, special, or 
            consequential damages arising out of or in any way connected with your use of the 
            calculators or the information provided, whether based on warranty, contract, tort, 
            or any other legal theory, and whether or not EquiForge™ is advised of the possibility 
            of such damages.
          </p>
        </section>

        <section id="contact">
          <h2>15. Contact Information</h2>
          <p>
            If you have any questions about these disclaimers or need clarification about our 
            calculators, please contact us at: <strong>legal@equiforge.com</strong>
          </p>
          <p>
            For technical support with the calculators: <strong>support@equiforge.com</strong>
          </p>
        </section>
      </div>
    </ModernPolicyLayout>
  );
};

export default Disclaimers;
